import {
    Banner,
    BlockStack,
    Button,
    Card,
    DataTable,
    InlineStack,
    Layout,
    Modal,
    Page,
    SkeletonBodyText,
    Text,
} from '@shopify/polaris';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import { LegacyAccount, LegacyOperationHistory } from '../../app/models/LegacyMode';
import { useLegacyMode } from './LegacyModeContext';
export { LegacyDocumentationPage } from './LegacyDocumentationPage';

export const LegacyDataExportPage = () => {
    const navigate = useNavigate();
    return (
        <Page title="Data export" subtitle="Use the V2 export workflow and history while your storefront remains in Legacy mode.">
            <Layout>
                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">Vehicle and fitment export</Text>
                            <Text as="p" tone="subdued">Exports run in the background and remain available from export history.</Text>
                            <Banner tone="info"><p>Legacy product-tag compatibility is preserved. The export workflow uses the newer app’s queue, status, and download handling.</p></Banner>
                            <InlineStack align="start"><Button variant="primary" onClick={() => navigate('/imports?view=exports')}>Open exports</Button></InlineStack>
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export const LegacyUniversalPage = () => {
    const [history, setHistory] = useState<LegacyOperationHistory[]>([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'success' | 'critical'; text: string }>();

    const load = async () => {
        try { setHistory(await agent.LegacyAdmin.operations()); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const refresh = async () => {
        setRunning(true); setNotice(undefined);
        try {
            const result = await agent.LegacyAdmin.refreshUniversal();
            setNotice({ tone: 'success', text: result.message });
            await load();
        } catch (error) {
            setNotice({ tone: 'critical', text: await getApiErrorMessage(error, 'Unable to refresh universal products.') });
        } finally { setRunning(false); }
    };

    const rows = history.filter(item => item.operationType === 'UniversalRefresh').map(item => [
        new Date(item.createdAt).toLocaleString(), item.status, item.error || '—',
    ]);

    return (
        <Page title="Universal mapping" subtitle="Refresh products that already use the legacy universal tag.">
            <Layout>
                {notice && <Layout.Section><Banner tone={notice.tone}><p>{notice.text}</p></Banner></Layout.Section>}
                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="p">Products tagged <code>universal</code> are recognized as fitting every vehicle by the V2 legacy runtime.</Text>
                            <Text as="p" tone="subdued">Refreshing does not expand every vehicle into separate Shopify tags, which avoids Shopify’s product tag limit.</Text>
                            <InlineStack align="start"><Button variant="primary" loading={running} onClick={refresh}>Refresh universal products</Button></InlineStack>
                        </BlockStack>
                    </Card>
                </Layout.Section>
                <Layout.Section>
                    <Card>
                        <BlockStack gap="300">
                            <Text as="h2" variant="headingMd">History</Text>
                            {loading ? <SkeletonBodyText lines={3} /> : rows.length ? <DataTable columnContentTypes={['text', 'text', 'text']} headings={['Started', 'Status', 'Error']} rows={rows} /> : <Text as="p" tone="subdued">No universal refreshes yet.</Text>}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export const LegacyAccountPage = () => {
    const [account, setAccount] = useState<LegacyAccount>();
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'success' | 'critical'; text: string }>();

    const load = async () => {
        setLoading(true);
        try { setAccount(await agent.LegacyAdmin.account()); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const deleteVehicles = async () => {
        setDeleting(true); setNotice(undefined);
        try {
            const result = await agent.LegacyAdmin.deleteAllVehicles();
            setNotice({ tone: 'success', text: `Deleted ${result.deleted} legacy vehicle rows. Shopify product tags were left unchanged.` });
            setConfirming(false);
            await load();
        } catch (error) {
            setNotice({ tone: 'critical', text: await getApiErrorMessage(error, 'Unable to delete legacy vehicles.') });
        } finally { setDeleting(false); }
    };

    return (
        <Page title="Legacy account">
            <Layout>
                {notice && <Layout.Section><Banner tone={notice.tone}><p>{notice.text}</p></Banner></Layout.Section>}
                <Layout.Section>
                    <Card>
                        {loading ? <SkeletonBodyText lines={4} /> : (
                            <BlockStack gap="400">
                                <Text as="h2" variant="headingMd">Database</Text>
                                <Text as="p">Store: {account?.shopDomain}</Text>
                                <Text as="p">Vehicle entries: {account?.vehicleEntryCount.toLocaleString()}</Text>
                                <Text as="p">Synchronized products: {account?.productCount.toLocaleString()}</Text>
                                <Text as="p">Synchronized product tags: {account?.productTagCount.toLocaleString()}</Text>
                                <InlineStack align="start"><Button tone="critical" onClick={() => setConfirming(true)}>Delete all vehicles</Button></InlineStack>
                            </BlockStack>
                        )}
                    </Card>
                </Layout.Section>
            </Layout>
            <Modal
                open={confirming}
                onClose={() => setConfirming(false)}
                title="Delete all legacy vehicles?"
                primaryAction={{ content: 'Delete all vehicles', destructive: true, loading: deleting, onAction: deleteVehicles }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setConfirming(false) }]}
            ><Modal.Section><Text as="p">This removes the vehicle hierarchy from the app. It does not remove existing vehicle tags from Shopify products.</Text></Modal.Section></Modal>
        </Page>
    );
};

export const LegacyTurn14Page = () => {
    const navigate = useNavigate();
    return (
        <Page title="Turn14 integration">
            <Layout><Layout.Section><Card><BlockStack gap="400">
                <Text as="h2" variant="headingMd">Turn14 fitment data</Text>
                <Text as="p">Your prior Turn14 activity was detected. Continue importing compatible CSV/XLSX files through the newer Bulk Import workflow.</Text>
                <Banner tone="warning"><p>Before the first live migration, verify one recent Turn14 file against a copied store dataset and confirm the resulting legacy vehicle tags.</p></Banner>
                <InlineStack align="start"><Button variant="primary" onClick={() => navigate('/imports')}>Open Bulk Import</Button></InlineStack>
            </BlockStack></Card></Layout.Section></Layout>
        </Page>
    );
};

export const LegacyUpgradeReviewPage = () => {
    const { status, loading } = useLegacyMode();
    const readiness = useMemo(() => [
        ['Legacy vehicle data', (status?.legacyVehicleCount ?? 0) > 0, `${status?.legacyVehicleCount ?? 0} rows`],
        ['Storefront settings', status?.hasStorefrontTheme ?? false, status?.hasStorefrontTheme ? 'Available' : 'Missing'],
        ['Legacy settings snapshot', status?.hasLegacyThemeSnapshot ?? false, status?.hasLegacyThemeSnapshot ? 'Available' : 'Missing'],
        ['Product tags synchronized', (status?.synchronizedProductTagCount ?? 0) > 0, `${status?.synchronizedProductTagCount ?? 0} tags`],
        ['Customer garage audit', status?.customerGarageMigrationCompleted ?? false, status?.customerGarageMigrationCompleted ? 'Complete' : 'Required'],
    ], [status]);

    return (
        <Page title="Review upgrade" subtitle="Nothing on this page changes your storefront mode.">
            <Layout>
                <Layout.Section>
                    <Banner tone="info" title="Upgrade is controlled by GoVedia"><p>We will analyze and test each legacy store before enabling the native fitment experience. There is intentionally no immediate upgrade button.</p></Banner>
                </Layout.Section>
                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">Readiness checks</Text>
                            {loading ? <SkeletonBodyText lines={5} /> : <DataTable columnContentTypes={['text', 'text', 'text']} headings={['Check', 'Status', 'Details']} rows={readiness.map(([label, ok, detail]) => [String(label), ok ? 'Ready' : 'Needs review', String(detail)])} />}
                            {!!status?.warnings?.length && <Banner tone="warning" title="Items requiring review"><ul>{status.warnings.map(warning => <li key={warning}>{warning}</li>)}</ul></Banner>}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};
